// EventsTable.js
import {
    Box, Button, Flex, Heading, IconButton, Input, Text,
    InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiPlus, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import EventsService from '../../../../services/events/eventsService';
import DateRangeSelector from "../../../../shared/components/DateRangeSelector/DateRangeSelector";
import EventsCardList from "../../components/EventsCardList/EventsCardList";
import { format } from 'date-fns';
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../../../hooks/useAppToast";

const EventsTable = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');

    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [sort, setSort] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');
    const toast = useAppToast();

    const user = JSON.parse(localStorage.getItem("user"));
    const isSuperAdmin = user?.roles?.includes('SuperAdmin') ?? false;

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents({
                page,
                search,
                dateFrom: dateFrom ? format(new Date(dateFrom), 'yyyy-MM-dd') : undefined,
                dateTo: dateTo ? format(new Date(dateTo), 'yyyy-MM-dd') : undefined,
                createdFrom: createdFrom ? format(new Date(createdFrom), 'yyyy-MM-dd') : undefined,
                createdTo: createdTo ? format(new Date(createdTo), 'yyyy-MM-dd') : undefined,
                sort: (sort + "_" + sortDir),
            });
            setEvents(res.data.data.items);
            setTotalPages(res.data.data.totalPages);
        } catch (error) {
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [page, sort, sortDir, dateFrom, dateTo, createdFrom, createdTo]);

    const handleSearch = (e) => {
        e?.preventDefault();
        if (page !== 1) setPage(1);
        else fetchEvents();
    };

    // Numery stron: pierwsza, ostatnia i okolice bieżącej — reszta jako "…"
    const pageItems = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p) => {
            if (acc.length && p - acc[acc.length - 1] > 1) acc.push('…');
            acc.push(p);
            return acc;
        }, []);

    const handleSort = (column) => {
        if (sort === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    return (
        <Box w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} py={{ base: '24px', md: '48px' }}>
            <Flex justify="space-between" alignItems={{ base: 'stretch', md: 'flex-end' }} flexDirection={{ base: 'column', md: 'row' }}
                mb={{ base: '20px', md: '28px' }} gap="16px">
                <Heading as="h1" fontWeight={700} fontSize={{ base: '30px', md: '40px' }} letterSpacing="-1px">
                    {t('event.table.title')}
                </Heading>
                <Button leftIcon={<FiPlus />} bg="brand.500" color="white" h="48px" px="22px" borderRadius="14px"
                    _hover={{ bg: 'brand.600' }} onClick={() => navigate('/events/new')}
                    w={{ base: '100%', md: 'auto' }}>
                    {t('event.new')}
                </Button>
            </Flex>

            <Flex bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius="18px" p={{ base: '12px', md: '14px' }}
                mb={{ base: '20px', md: '28px' }} gap={{ base: '12px', md: '16px' }} flexDirection={{ base: 'column', lg: 'row' }} alignItems={{ base: 'stretch', lg: 'center' }}>
                <Flex as="form" onSubmit={handleSearch} gap="8px" flex="1" role="search">
                    <InputGroup>
                        <InputLeftElement pointerEvents="none" h="44px">
                            <FiSearch color="#6E6064" />
                        </InputLeftElement>
                        <Input
                            placeholder={t('event.name')}
                            aria-label={t('event.name')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            bg="white"
                            h="44px"
                        />
                    </InputGroup>
                    <Button type="submit" bg="brand.500" color="white" h="44px" px="20px" flexShrink={0}
                        _hover={{ bg: 'brand.600' }}>
                        {t('event.table.search')}
                    </Button>
                </Flex>
                <Flex gap={{ base: '12px', md: '24px' }} flexWrap="wrap">
                    <DateRangeSelector
                        label={t('event.createdAt')}
                        fromDate={createdFrom}
                        toDate={createdTo}
                        onChange={({ from, to }) => {
                            setCreatedFrom(from);
                            setPage(1);
                            setCreatedTo(to);
                        }}
                    />
                    <DateRangeSelector
                        label={t('event.startDate')}
                        fromDate={dateFrom}
                        toDate={dateTo}
                        onChange={({ from, to }) => {
                            setDateFrom(from);
                            setPage(1);
                            setDateTo(to);
                        }}
                    />
                </Flex>
            </Flex>

            <EventsCardList
                events={events}
                sort={sort}
                sortDir={sortDir}
                onSort={handleSort}
                loading={loading}
                isSuperAdmin={isSuperAdmin}
            />

            {totalPages > 1 &&
                <Flex as="nav" aria-label="Paginacja" justify="center" alignItems="center" gap={'6px'} my={'40px'} flexWrap="wrap">
                    <IconButton aria-label="Poprzednia strona" icon={<FiChevronLeft />} onClick={() => setPage(Math.max(1, page - 1))}
                        isDisabled={page === 1} variant="outline" bg="white" borderColor="surface.control" color="ink.900"
                        w="40px" h="40px" minW="40px" borderRadius="10px" _hover={{ bg: 'surface.page', transform: 'none', boxShadow: 'none' }} />
                    {pageItems.map((p, idx) => p === '…'
                        ? <Text key={`gap-${idx}`} px="4px" color="ink.500">…</Text>
                        : <Button key={p} onClick={() => setPage(p)} aria-current={page === p ? 'page' : undefined}
                            w="40px" h="40px" minW="40px" borderRadius="10px" fontWeight={600}
                            bg={page === p ? 'brand.500' : 'white'} color={page === p ? 'white' : 'ink.900'}
                            border="1px solid" borderColor={page === p ? 'brand.500' : 'surface.control'}
                            _hover={{ bg: page === p ? 'brand.500' : 'surface.page', transform: 'none', boxShadow: 'none' }}>
                            {p}
                        </Button>
                    )}
                    <IconButton aria-label="Następna strona" icon={<FiChevronRight />} onClick={() => setPage(Math.min(totalPages, page + 1))}
                        isDisabled={page === totalPages} variant="outline" bg="white" borderColor="surface.control" color="ink.900"
                        w="40px" h="40px" minW="40px" borderRadius="10px" _hover={{ bg: 'surface.page', transform: 'none', boxShadow: 'none' }} />
                </Flex>
            }

        </Box>
    );
};

export default EventsTable;
